import * as bitcoin from 'bitcoinjs-lib'
import {
	NUMS_INTERNAL_KEY_HEX,
	PINNED_COSIGNER_XONLY_HEX,
	TAPSCRIPT_LEAF_VERSION,
	parsePassthroughLeaf,
	passthroughEscrow
} from './secure-escrow'
import {
	parsePsbt,
	unsignedTxHexFromPsbt,
	type SecureInvoiceBreakdown,
	type SecurePurchaseItemBreakdown,
	finalTxidFromPsbt
} from './secure-psbt'
import { SecurePurchaseUnavailableError, SIGHASH_SINGLE_ANYONECANPAY } from './secure-purchase'

/** Fee a passthrough may pay when the postage can spare it (0.1 sat/vB relay floor). */
const PASSTHROUGH_PARENT_FEE_SATS = 12

/**
 * Passthrough v4 verification, client side. Nothing here trusts the API: the
 * seller checks the two templates they are asked to sign against their own
 * inputs, and the buyer checks the sale against the listings they chose and
 * the pinned co-signer before signing their funding.
 * See docs/PASSTHROUGH_V4.md in the API repository.
 */

const MIN_ESCROW_VALUE = 330
const NETWORK = bitcoin.networks.bitcoin

function scriptsEqual(a: Buffer | Uint8Array, b: Buffer | Uint8Array): boolean {
	return Buffer.from(a).equals(Buffer.from(b))
}

function outpointOf(psbt: bitcoin.Psbt, index: number): string {
	const input = psbt.txInputs[index]
	return `${Buffer.from(input.hash).reverse().toString('hex')}:${input.index}`
}

function outputScriptForAddress(address: string): Buffer {
	return bitcoin.address.toOutputScript(address, NETWORK)
}

// ---- seller side --------------------------------------------------------------

type ListingTemplateCheck = {
	passthroughPsbtHex: string
	salePsbtHex: string
	expectedOutpoint: string
	sellerXOnly: Buffer
	expectedSellerScript: Buffer
	expectedSellerSats: number
}

/**
 * The two templates a seller signs. Passthrough: their inscription UTXO into
 * an escrow rebuilt locally from their key and the pinned co-signer, one
 * input and one output, ZERO fee, so the postage moves whole; the buyer's
 * sale pays for it as a package. Sale template: that escrow as the only
 * input, paying exactly the seller the price they typed, to be signed on the
 * script path with SIGHASH_SINGLE|ANYONECANPAY.
 */
export function assertListingTemplates(input: ListingTemplateCheck): {
	escrowValue: number
	passthroughTxid: string
} {
	const escrow = passthroughEscrow(input.sellerXOnly)
	const passthrough = parsePsbt(input.passthroughPsbtHex)
	if (passthrough.inputCount !== 1) {
		throw new SecurePurchaseUnavailableError(
			'listing_input_count',
			'Passthrough must spend exactly the item'
		)
	}
	if (outpointOf(passthrough, 0).toLowerCase() !== input.expectedOutpoint.toLowerCase()) {
		throw new SecurePurchaseUnavailableError(
			'listing_input_mismatch',
			'Passthrough spends a different UTXO than the item you selected'
		)
	}
	const postage = passthrough.data.inputs[0]?.witnessUtxo?.value
	if (typeof postage !== 'number') {
		throw new SecurePurchaseUnavailableError(
			'missing_witness_utxo',
			'Passthrough input is missing its prevout'
		)
	}
	const outputs = passthrough.txOutputs
	if (outputs.length !== 1) {
		throw new SecurePurchaseUnavailableError(
			'listing_output_shape',
			'Passthrough has an unexpected output'
		)
	}
	const escrowOut = outputs[0]
	if (!scriptsEqual(escrowOut.script, escrow.output)) {
		throw new SecurePurchaseUnavailableError(
			'listing_escrow_mismatch',
			'Passthrough does not send the item to your own passthrough escrow'
		)
	}
	const escrowValue = Number(escrowOut.value)
	// Zero fee, or the 12-sat (0.1 sat/vB) relay floor shaved off the tail of
	// the postage when it can spare it. Nothing else.
	const shaved = postage - escrowValue
	if ((shaved !== 0 && shaved !== PASSTHROUGH_PARENT_FEE_SATS) || escrowValue < MIN_ESCROW_VALUE) {
		throw new SecurePurchaseUnavailableError(
			'listing_postage_mismatch',
			`Passthrough must move the postage whole (or shave exactly ${PASSTHROUGH_PARENT_FEE_SATS} sats); it moves ${escrowValue} of ${postage} sats`
		)
	}
	const sighash = passthrough.data.inputs[0].sighashType
	if (
		sighash !== undefined &&
		sighash !== bitcoin.Transaction.SIGHASH_DEFAULT &&
		sighash !== bitcoin.Transaction.SIGHASH_ALL
	) {
		throw new SecurePurchaseUnavailableError(
			'listing_sighash',
			'Passthrough must be signed with SIGHASH_ALL'
		)
	}
	const passthroughTxid = bitcoin.Transaction.fromHex(
		unsignedTxHexFromPsbt(input.passthroughPsbtHex)
	).getId()

	const sale = parsePsbt(input.salePsbtHex)
	if (sale.inputCount !== 1 || sale.txOutputs.length !== 1) {
		throw new SecurePurchaseUnavailableError(
			'sale_template_shape',
			'Sale template must have one input and one output'
		)
	}
	if (outpointOf(sale, 0) !== `${passthroughTxid}:0`) {
		throw new SecurePurchaseUnavailableError(
			'sale_template_input',
			'Sale template does not spend the passthrough output'
		)
	}
	const saleInput = sale.data.inputs[0]
	if (
		!saleInput.witnessUtxo ||
		!scriptsEqual(saleInput.witnessUtxo.script, escrow.output) ||
		Number(saleInput.witnessUtxo.value) !== escrowValue
	) {
		throw new SecurePurchaseUnavailableError(
			'sale_template_prevout',
			'Sale template prevout is not the passthrough escrow'
		)
	}
	if (saleInput.sighashType !== SIGHASH_SINGLE_ANYONECANPAY) {
		throw new SecurePurchaseUnavailableError(
			'sale_template_sighash',
			'Sale template must be signed with SIGHASH_SINGLE|ANYONECANPAY (0x83)'
		)
	}
	const leafs = saleInput.tapLeafScript || []
	const ours = leafs.find(
		(entry) =>
			scriptsEqual(entry.script, escrow.leaf) && entry.leafVersion === TAPSCRIPT_LEAF_VERSION
	)
	if (!ours) {
		throw new SecurePurchaseUnavailableError(
			'sale_template_leaf',
			'Sale template does not carry your escrow leaf'
		)
	}
	// The escrow has no key path; a template naming any other internal key
	// (your own included) would make your wallet sign the wrong thing.
	if (
		!saleInput.tapInternalKey ||
		!Buffer.from(saleInput.tapInternalKey).equals(escrow.internalKey)
	) {
		throw new SecurePurchaseUnavailableError(
			'sale_template_internal_key',
			'Sale template internal key is not the unspendable escrow key'
		)
	}
	const payout = sale.txOutputs[0]
	if (!scriptsEqual(payout.script, input.expectedSellerScript)) {
		throw new SecurePurchaseUnavailableError(
			'listing_payout_mismatch',
			'Sale template pays someone other than your wallet'
		)
	}
	if (Number(payout.value) !== input.expectedSellerSats) {
		throw new SecurePurchaseUnavailableError(
			'listing_price_mismatch',
			`Sale template pays ${Number(payout.value)} sats, not the ${
				input.expectedSellerSats
			} you asked for`
		)
	}
	return { escrowValue, passthroughTxid }
}

/** Codes meaning the wallet cannot produce the script-path signature a protected listing needs. */
export const PROTECTED_SIGNING_UNSUPPORTED_CODES = new Set([
	'sale_signed_on_key_path',
	'sale_template_sighash',
	'sale_unsigned'
])

export function isProtectedSigningUnsupported(error: unknown): boolean {
	return (
		error instanceof SecurePurchaseUnavailableError &&
		PROTECTED_SIGNING_UNSUPPORTED_CODES.has(error.code)
	)
}

/**
 * Check the seller's signature on the sale template and return the PSBT the
 * API will accept. Some wallets (OKX) ignore `disableTweakSigner` and add a
 * key-path signature alongside the script-path one; that stray signature is
 * dropped here, because the API refuses any template carrying one. Only a
 * template with no script-path signature at all is rejected.
 */
export function sanitizeSignedSaleTemplate(signedSalePsbtHex: string, sellerXOnly: Buffer): string {
	const psbt = parsePsbt(signedSalePsbtHex)
	const input = psbt.data.inputs[0]
	const hadKeyPath = Boolean(input.tapKeySig && input.tapKeySig.length)
	const sigs = input.tapScriptSig || []
	const mine = sigs.find((entry) => Buffer.from(entry.pubkey).equals(sellerXOnly))
	if (mine) {
		const signature = Buffer.from(mine.signature)
		if (signature.length !== 65 || signature[64] !== SIGHASH_SINGLE_ANYONECANPAY) {
			throw new SecurePurchaseUnavailableError(
				'sale_template_sighash',
				'Sale signature does not commit with SIGHASH_SINGLE|ANYONECANPAY'
			)
		}
		if (hadKeyPath) {
			delete input.tapKeySig
			return psbt.toHex()
		}
		return signedSalePsbtHex
	}
	// Some wallets finalize even a partial script path; accept a witness that
	// carries a 65-byte 0x83 signature, the API verifies it against the key.
	if (input.finalScriptWitness) {
		const raw = Buffer.from(input.finalScriptWitness)
		if (raw.includes(Buffer.from([SIGHASH_SINGLE_ANYONECANPAY]))) {
			if (hadKeyPath) {
				delete input.tapKeySig
				return psbt.toHex()
			}
			return signedSalePsbtHex
		}
	}
	if (hadKeyPath) {
		throw new SecurePurchaseUnavailableError(
			'sale_signed_on_key_path',
			'Wallet signed the sale template on the key path only'
		)
	}
	throw new SecurePurchaseUnavailableError(
		'sale_unsigned',
		'Wallet returned the sale template without a script-path signature'
	)
}

/** Throwing form of `sanitizeSignedSaleTemplate`, kept for callers that only check. */
export function assertSignedSaleTemplate(signedSalePsbtHex: string, sellerXOnly: Buffer): void {
	sanitizeSignedSaleTemplate(signedSalePsbtHex, sellerXOnly)
}

// ---- buyer side ---------------------------------------------------------------

/**
 * A listing's passthrough as the build returns it. `raw` carries no witness:
 * the seller-signed passthrough is a griefing capability (anyone holding it
 * can push the item into its escrow), so the API keeps it for the submit
 * package and serves the unsigned form. The txid, the one input and the
 * escrow output are all the buyer checks, and none depends on the witness.
 */
export type SaleParent = { txid: string; raw: string; source_outpoint: string }

type SaleListing = {
	/** The outpoint the buyer chose. */
	outpoint: string
	sellerAddress: string
	creatorAddress?: string | null
	/** Buyer-facing price shown in the UI, when known. */
	satoshiPrice?: number
}

type SaleVerification = SecureInvoiceBreakdown & {
	items: SecurePurchaseItemBreakdown[]
	/** Input index of each item's passthrough, in listing order. */
	passthroughInputs: number[]
	assetOutputs: number[]
	buyerInputs: number[]
}

/**
 * Verify a co-signed sale before the buyer signs anything.
 *
 * For each listing: a parent that spends exactly the chosen outpoint and pays
 * an escrow whose leaf names the pinned co-signer; a sale input spending that
 * parent's output 0 whose witness is already complete; the output at the same
 * index paying the listing's seller; an asset output paying this buyer the
 * escrow value, positioned so the sats line up; buyer inputs only at the ends;
 * every other output either the marketplace fee, a listed creator's royalty,
 * or the buyer's change.
 */
export function verifySale(input: {
	salePsbtHex: string
	parents: SaleParent[]
	listings: SaleListing[]
	buyerAddresses: string[]
	marketFeeAddress: string
	/** Chained sale: both buyer inputs must spend this earlier sale's outputs. */
	fundingTxid?: string
	/**
	 * Whether this transaction carries the marketplace fee and royalties (the
	 * first link of a chain does; later links do not). Needed only when the
	 * buyer is the marketplace address itself, where script alone cannot tell
	 * fee from change.
	 */
	carriesMarketFee?: boolean
}): SaleVerification {
	const sale = parsePsbt(input.salePsbtHex)
	const n = input.listings.length
	if (n === 0) throw new SecurePurchaseUnavailableError('invalid_sale', 'Sale has no items')
	if (input.parents.length !== n) {
		throw new SecurePurchaseUnavailableError(
			'invalid_sale',
			'Sale did not come with one passthrough per item'
		)
	}
	if (sale.inputCount < n + 2) {
		throw new SecurePurchaseUnavailableError(
			'sale_input_count',
			'Sale must spend each passthrough plus at least two of your funding inputs'
		)
	}
	const buyerScripts = input.buyerAddresses.filter(Boolean).map(outputScriptForAddress)
	const ownedByBuyer = (script: Buffer | Uint8Array) =>
		buyerScripts.some((s) => scriptsEqual(s, script))
	const marketScript = outputScriptForAddress(input.marketFeeAddress)
	const cosigner = Buffer.from(PINNED_COSIGNER_XONLY_HEX, 'hex')

	// Parents, keyed by txid.
	const parentByTxid = new Map<string, bitcoin.Transaction>()
	input.parents.forEach((parent) => {
		const tx = bitcoin.Transaction.fromHex(parent.raw)
		if (tx.getId() !== parent.txid) {
			throw new SecurePurchaseUnavailableError(
				'parent_txid_mismatch',
				'A passthrough does not hash to its declared txid'
			)
		}
		parentByTxid.set(tx.getId(), tx)
	})

	const inputValues: number[] = sale.data.inputs.map((data, index) => {
		const value = data.witnessUtxo?.value
		if (typeof value !== 'number') {
			throw new SecurePurchaseUnavailableError(
				'missing_witness_utxo',
				`Sale input ${index} is missing its prevout`
			)
		}
		return Number(value)
	})
	const outputs = sale.txOutputs.map((o) => ({
		script: Buffer.from(o.script),
		value: Number(o.value)
	}))

	// Layout: leading buyer inputs, then exactly the passthroughs as one
	// block, then trailing buyer inputs (at least one of each side). Assets
	// land at the same offsets whatever the counts, because outputs 0..n sum
	// to the leading inputs.
	const isPassthroughInput = (index: number) =>
		parentByTxid.has(outpointOf(sale, index).split(':')[0])
	const firstPassthrough = Array.from({ length: sale.inputCount }, (_, i) => i).findIndex(
		isPassthroughInput
	)
	if (firstPassthrough < 1) {
		throw new SecurePurchaseUnavailableError(
			'sale_input_count',
			'Sale must start with at least one of your funding inputs'
		)
	}
	for (let index = firstPassthrough; index < firstPassthrough + n; index += 1) {
		if (!isPassthroughInput(index)) {
			throw new SecurePurchaseUnavailableError(
				'sale_input_not_passthrough',
				`Sale input ${index} does not spend a passthrough`
			)
		}
	}
	if (firstPassthrough + n >= sale.inputCount) {
		throw new SecurePurchaseUnavailableError(
			'sale_input_count',
			'Sale must end with at least one of your fee inputs'
		)
	}
	const buyerInputs = Array.from({ length: sale.inputCount }, (_, i) => i).filter(
		(i) => i < firstPassthrough || i >= firstPassthrough + n
	)
	for (const index of buyerInputs) {
		const script = sale.data.inputs[index].witnessUtxo?.script
		if (!script || !ownedByBuyer(script)) {
			throw new SecurePurchaseUnavailableError(
				'funding_not_yours',
				`Sale input ${index} is not one of your UTXOs`
			)
		}
		if (sale.data.inputs[index].finalScriptWitness) {
			throw new SecurePurchaseUnavailableError(
				'funding_prefilled',
				'Your funding input already carries a witness'
			)
		}
		if (
			input.fundingTxid &&
			!outpointOf(sale, index).toLowerCase().startsWith(`${input.fundingTxid.toLowerCase()}:`)
		) {
			throw new SecurePurchaseUnavailableError(
				'chain_funding_mismatch',
				`Sale input ${index} does not spend the previous sale in this purchase`
			)
		}
	}

	const passthroughInputs: number[] = []
	const assetOutputs: number[] = []
	const items: SecurePurchaseItemBreakdown[] = []
	let sellerProceedsSat = 0
	let inOffset = inputValues.slice(0, firstPassthrough).reduce((a, b) => a + b, 0)
	const outOffsets: number[] = []
	outputs.reduce((acc, o) => {
		outOffsets.push(acc)
		return acc + o.value
	}, 0)

	input.listings.forEach((listing, i) => {
		const index = firstPassthrough + i
		const data = sale.data.inputs[index]
		const outpoint = outpointOf(sale, index)
		const [parentTxid, vout] = outpoint.split(':')
		const parent = parentByTxid.get(parentTxid)
		if (!parent || vout !== '0') {
			throw new SecurePurchaseUnavailableError(
				'sale_input_not_passthrough',
				`Sale input ${index} does not spend a passthrough`
			)
		}
		// The parent must spend exactly the listing the buyer chose.
		const parentInput = parent.ins[0]
		const parentSpends = `${Buffer.from(parentInput.hash).reverse().toString('hex')}:${
			parentInput.index
		}`
		if (
			parent.ins.length !== 1 ||
			parent.outs.length !== 1 ||
			parentSpends.toLowerCase() !== listing.outpoint.toLowerCase()
		) {
			throw new SecurePurchaseUnavailableError(
				'parent_source_mismatch',
				`Passthrough ${i + 1} does not move the item you selected`
			)
		}
		const escrowOut = parent.outs[0]
		const escrowValue = Number(escrowOut.value)
		if (
			!data.witnessUtxo ||
			!scriptsEqual(data.witnessUtxo.script, escrowOut.script) ||
			Number(data.witnessUtxo.value) !== escrowValue
		) {
			throw new SecurePurchaseUnavailableError(
				'sale_prevout_mismatch',
				`Sale input ${index} misstates its passthrough prevout`
			)
		}
		// The passthrough input is either already co-signed (complete witness)
		// or carries the leaf as metadata for Ordinals Wallet to co-sign at
		// submit. Either way the leaf must name the pinned co-signer and the
		// prevout must be the escrow that leaf implies.
		let leaf: Buffer
		const witness = data.finalScriptWitness
		if (witness) {
			const items4 = witnessStack(Buffer.from(witness))
			if (items4.length !== 4) {
				throw new SecurePurchaseUnavailableError(
					'sale_witness_shape',
					`Sale input ${index} witness is not a 2-of-2 script path`
				)
			}
			const [cosig, sellersig] = items4
			leaf = items4[2]
			if (
				cosig.length !== 64 ||
				sellersig.length !== 65 ||
				sellersig[64] !== SIGHASH_SINGLE_ANYONECANPAY
			) {
				throw new SecurePurchaseUnavailableError(
					'sale_witness_sighash',
					`Sale input ${index} signatures have unexpected sighash types`
				)
			}
		} else {
			const entry = (data.tapLeafScript || []).find(
				(e) => e.leafVersion === TAPSCRIPT_LEAF_VERSION && parsePassthroughLeaf(e.script)
			)
			if (!entry) {
				throw new SecurePurchaseUnavailableError(
					'sale_not_cosigned',
					`Sale input ${index} carries neither a co-signed witness nor its escrow leaf`
				)
			}
			leaf = Buffer.from(entry.script)
			if (
				!data.tapInternalKey ||
				!Buffer.from(data.tapInternalKey).equals(Buffer.from(NUMS_INTERNAL_KEY_HEX, 'hex'))
			) {
				throw new SecurePurchaseUnavailableError(
					'sale_template_internal_key',
					`Sale input ${index} does not use the unspendable escrow key`
				)
			}
		}
		const keys = parsePassthroughLeaf(leaf)
		if (!keys || !keys.cosigner.equals(cosigner)) {
			throw new SecurePurchaseUnavailableError(
				'sale_leaf_unpinned',
				`Sale input ${index} is not co-signed by Ordinals Wallet's pinned key`
			)
		}
		const rebuilt = passthroughEscrow(keys.seller, keys.cosigner)
		if (!scriptsEqual(rebuilt.output, escrowOut.script)) {
			throw new SecurePurchaseUnavailableError(
				'sale_escrow_mismatch',
				`Passthrough ${i + 1} escrow does not match its leaf`
			)
		}
		// Payout at the same index as the passthrough input.
		const payout = outputs[index]
		const sellerScript = outputScriptForAddress(listing.sellerAddress)
		if (!payout || !scriptsEqual(payout.script, sellerScript)) {
			throw new SecurePurchaseUnavailableError(
				'sale_payout_mismatch',
				`Sale output ${index} does not pay item ${i + 1}'s seller`
			)
		}
		// Asset output at the same sat offset as the passthrough's first sat.
		const assetIndex = outOffsets.findIndex((offset, oi) => offset === inOffset && oi > n)
		const asset = assetIndex >= 0 ? outputs[assetIndex] : undefined
		if (!asset || asset.value !== escrowValue || !ownedByBuyer(asset.script)) {
			throw new SecurePurchaseUnavailableError(
				'sale_asset_mismatch',
				`Item ${i + 1} would not land in your wallet on its own sats`
			)
		}
		passthroughInputs.push(index)
		assetOutputs.push(assetIndex)
		sellerProceedsSat += payout.value
		items.push({
			sellerProceedsSat: payout.value,
			marketFeeSat: 0,
			creatorRoyaltySat: 0,
			itemTotalSat: payout.value
		})
		inOffset += escrowValue
	})

	// Remaining outputs: market fee, listed creators' royalties, buyer change.
	const creatorScripts = input.listings
		.map((l) => (l.creatorAddress ? outputScriptForAddress(l.creatorAddress) : null))
		.filter((s): s is Buffer => Boolean(s))
	let marketFeeSat = 0
	let creatorRoyaltySat = 0
	let changeSat = 0
	const accounted = new Set<number>(assetOutputs)
	// Output 0 is change_1 by layout (the leading inputs minus the payouts).
	// When the buyer IS the marketplace address (treasury buying), script
	// alone cannot tell fee from change: the last output is change_2 once a
	// fee output has been seen.
	const buyerIsMarket = ownedByBuyer(marketScript)
	const carriesMarketFee = input.carriesMarketFee ?? true
	let marketSeen = false
	outputs.forEach((o, index) => {
		if (accounted.has(index) || passthroughInputs.includes(index)) return
		if (index === 0) {
			if (!ownedByBuyer(o.script)) {
				throw new SecurePurchaseUnavailableError(
					'sale_change_mismatch',
					'Sale output 0 is not your change'
				)
			}
			changeSat += o.value
			return
		}
		// Treasury buying: the first market-script output after the assets is
		// the fee when this transaction carries one; everything else to that
		// script is the buyer's change.
		const marketOutput = scriptsEqual(o.script, marketScript)
		const isFee = marketOutput && (!buyerIsMarket || (carriesMarketFee && !marketSeen))
		if (isFee) {
			marketFeeSat += o.value
			marketSeen = true
		} else if (creatorScripts.some((s) => scriptsEqual(s, o.script))) {
			creatorRoyaltySat += o.value
		} else if (ownedByBuyer(o.script)) {
			changeSat += o.value
		} else {
			throw new SecurePurchaseUnavailableError(
				'sale_unknown_output',
				`Sale output ${index} pays an unexpected party`
			)
		}
	})
	const totalIn = inputValues.reduce((a, b) => a + b, 0)
	const totalOut = outputs.reduce((a, o) => a + o.value, 0)
	const networkFeeSat = totalIn - totalOut
	if (networkFeeSat <= 0) {
		throw new SecurePurchaseUnavailableError('sale_fee', 'Sale outputs exceed its inputs')
	}
	const itemTotalSat = sellerProceedsSat + marketFeeSat + creatorRoyaltySat
	const listed = input.listings.map((l) => Number(l.satoshiPrice || 0))
	if (listed.every((v) => v > 0)) {
		const expected = listed.reduce((a, b) => a + b, 0)
		if (sellerProceedsSat + marketFeeSat > expected) {
			throw new SecurePurchaseUnavailableError(
				'sale_overcharge',
				'Sale charges more than the listed prices'
			)
		}
	}
	return {
		sellerProceedsSat,
		marketFeeSat,
		creatorRoyaltySat,
		networkFeeSat,
		changeSat,
		itemTotalSat,
		totalSat: itemTotalSat + networkFeeSat,
		items,
		passthroughInputs,
		assetOutputs,
		buyerInputs
	}
}

function readVarInt(buffer: Buffer, offset: number): { value: number; size: number } {
	const first = buffer[offset]
	if (first < 0xfd) return { value: first, size: 1 }
	if (first === 0xfd) return { value: buffer.readUInt16LE(offset + 1), size: 3 }
	if (first === 0xfe) return { value: buffer.readUInt32LE(offset + 1), size: 5 }
	return { value: Number(buffer.readBigUInt64LE(offset + 1)), size: 9 }
}

export function witnessStack(finalScriptWitness: Buffer): Buffer[] {
	const items: Buffer[] = []
	let offset = 0
	const count = readVarInt(finalScriptWitness, offset)
	offset += count.size
	for (let i = 0; i < count.value; i++) {
		const length = readVarInt(finalScriptWitness, offset)
		offset += length.size
		items.push(finalScriptWitness.subarray(offset, offset + length.value))
		offset += length.value
	}
	return items
}

type SaleChainLink = {
	salePsbtHex: string
	parent: SaleParent
	listing: SaleListing
}

type SaleChainVerification = SecureInvoiceBreakdown & {
	items: SecurePurchaseItemBreakdown[]
	links: SaleVerification[]
	saleTxids: string[]
}

/**
 * A protected purchase is a chain of single-item sales: every sale spends
 * exactly one zero-fee passthrough (so the network relays it as a
 * parent+child package) plus two buyer inputs, which for every link after the
 * first are the previous link's two change outputs. The marketplace fee and
 * royalties sit in the first link only; the listed prices are checked against
 * the whole chain.
 */
type SetupVerification = { txid: string; feeSat: number; outputs: number[] }

/**
 * The buyer's setup transaction: their own inputs into exactly two outputs
 * at their own addresses, which the first link then spends. Nothing leaves
 * the wallet except the miner fee.
 */
export function verifySetup(input: {
	setupPsbtHex: string
	buyerAddresses: string[]
	feeRateSatVb: number
}): SetupVerification {
	const setup = parsePsbt(input.setupPsbtHex)
	const buyerScripts = input.buyerAddresses.filter(Boolean).map(outputScriptForAddress)
	const ownedByBuyer = (script: Buffer | Uint8Array) =>
		buyerScripts.some((s) => scriptsEqual(s, script))
	let totalIn = 0
	setup.data.inputs.forEach((data, index) => {
		if (!data.witnessUtxo || !ownedByBuyer(data.witnessUtxo.script)) {
			throw new SecurePurchaseUnavailableError(
				'setup_input_not_yours',
				`Setup input ${index} is not one of your UTXOs`
			)
		}
		if (data.finalScriptWitness) {
			throw new SecurePurchaseUnavailableError(
				'funding_prefilled',
				'Setup input already carries a witness'
			)
		}
		totalIn += Number(data.witnessUtxo.value)
	})
	if (setup.txOutputs.length !== 2) {
		throw new SecurePurchaseUnavailableError(
			'setup_output_shape',
			'Setup transaction must have exactly two outputs'
		)
	}
	const outputs = setup.txOutputs.map((o) => {
		if (!ownedByBuyer(o.script)) {
			throw new SecurePurchaseUnavailableError(
				'setup_output_not_yours',
				'Setup transaction pays someone other than you'
			)
		}
		return Number(o.value)
	})
	const feeSat = totalIn - outputs[0] - outputs[1]
	// ~58 vB per input + ~100 vB base, at the buyer's rate, with headroom.
	const maxFee =
		Math.ceil((100 + 58 * setup.inputCount) * Math.max(input.feeRateSatVb, 1) * 1.5) + 50
	if (feeSat <= 0 || feeSat > maxFee) {
		throw new SecurePurchaseUnavailableError(
			'setup_fee',
			`Setup transaction fee of ${feeSat} sats is out of range`
		)
	}
	return {
		txid: finalTxidFromPsbt(input.setupPsbtHex),
		feeSat,
		outputs
	}
}

export function verifySaleChain(input: {
	links: SaleChainLink[]
	buyerAddresses: string[]
	marketFeeAddress: string
	/** When the build added a setup transaction, link 1 must spend it. */
	setupTxid?: string
}): SaleChainVerification {
	if (input.links.length === 0)
		throw new SecurePurchaseUnavailableError('invalid_sale', 'Sale has no items')
	const links: SaleVerification[] = []
	const saleTxids: string[] = []
	let previous: string | undefined = input.setupTxid
	for (const link of input.links) {
		const v = verifySale({
			salePsbtHex: link.salePsbtHex,
			parents: [link.parent],
			listings: [{ ...link.listing, satoshiPrice: undefined }],
			buyerAddresses: input.buyerAddresses,
			marketFeeAddress: input.marketFeeAddress,
			fundingTxid: previous,
			carriesMarketFee: links.length === 0
		})
		links.push(v)
		previous = finalTxidFromPsbt(link.salePsbtHex)
		saleTxids.push(previous)
	}
	const sum = (pick: (v: SaleVerification) => number) => links.reduce((a, v) => a + pick(v), 0)
	const sellerProceedsSat = sum((v) => v.sellerProceedsSat)
	const marketFeeSat = sum((v) => v.marketFeeSat)
	const creatorRoyaltySat = sum((v) => v.creatorRoyaltySat)
	const networkFeeSat = sum((v) => v.networkFeeSat)
	const listed = input.links.map((l) => Number(l.listing.satoshiPrice || 0))
	if (listed.every((v) => v > 0)) {
		const expected = listed.reduce((a, b) => a + b, 0)
		if (sellerProceedsSat + marketFeeSat > expected) {
			throw new SecurePurchaseUnavailableError(
				'sale_overcharge',
				'Sale charges more than the listed prices'
			)
		}
	}
	const itemTotalSat = sellerProceedsSat + marketFeeSat + creatorRoyaltySat
	return {
		...links[links.length - 1],
		sellerProceedsSat,
		marketFeeSat,
		creatorRoyaltySat,
		networkFeeSat,
		changeSat: links[links.length - 1].changeSat,
		itemTotalSat,
		totalSat: itemTotalSat + networkFeeSat,
		items: links.flatMap((v) => v.items),
		links,
		saleTxids
	}
}
